import { Collapse, ConfigProvider, Divider, Flex, Modal, Typography } from "antd";
import {
  ModalHeader,
  modalButtonsTheme,
  modalSubtitleStyle,
} from "components/modals/ModalChrome";
import { getEmployeeManagementReactivationPreview } from "hooks/mutations/staff";
import { useCallback, useMemo, useState } from "react";
import { colors } from "shared/theme/color";
import { translate } from "shared/languages";
import {
  AffectedInstructorOccurrence,
  EmployeeManagementReactivationPreview,
} from "shared/types";
import { groupByDate } from "shared/utils";
import { localDateOnly, localTzDate } from "shared/utils/dayjs";
import { textPx } from "shared/constants/zoom";

const { Text } = Typography;

const REASON_LABEL_KEY: Record<AffectedInstructorOccurrence["reasons"][number], string> = {
  working_hours: "class_settings.employee_management.reactivation_conflict.reason_working_hours",
  leave: "class_settings.employee_management.reactivation_conflict.reason_leave",
};

// An occurrence can genuinely violate both at once (during leave AND outside
// working hours) — show every reason it actually has, not just the one that
// happened to be relevant to whichever toggle transition triggered this
// preview, so the admin sees the full picture.
const OccurrenceCard = ({ occurrence }: { occurrence: AffectedInstructorOccurrence }) => {
  const start = localTzDate(occurrence.occurrence_timestamp);
  const end = start.add(occurrence.duration, "minute");
  return (
    <div
      style={{
        padding: 12,
        border: `1px solid ${colors.lineStrong}`,
        borderRadius: 12,
        background: colors.card,
      }}
    >
      <Text style={{ fontWeight: 500 }}>{occurrence.class_name}</Text>
      <div>
        <Text type="secondary" style={{ fontSize: textPx(12.5) }}>
          {start.format("HH:mm")} - {end.format("HH:mm")}
        </Text>
        <Text type="secondary" style={{ fontSize: textPx(12.5) }}>
          {" "}
          · {occurrence.reasons.map((reason) => translate(REASON_LABEL_KEY[reason])).join(", ")}
        </Text>
      </div>
    </div>
  );
};

const DayGroup = ({
  date,
  occurrences,
}: {
  date: string;
  occurrences: AffectedInstructorOccurrence[];
}) => (
  <div>
    <Divider
      orientation="left"
      orientationMargin={0}
      style={{ marginTop: 12, marginBottom: 12 }}
    >
      <Text style={{ fontWeight: 500, fontSize: textPx(13) }}>
        {localDateOnly(date).format("dddd, DD MMMM YYYY")}
      </Text>
    </Divider>
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {occurrences.map((occurrence) => (
        <OccurrenceCard
          key={`${occurrence.schedule_id}-${occurrence.occurrence_timestamp}`}
          occurrence={occurrence}
        />
      ))}
    </div>
  </div>
);

// Name + count, used as the Collapse panel label so a business with many
// instructors still shows an at-a-glance summary (who's affected, how many
// classes each) instead of forcing a scroll through every occurrence of
// every instructor up front. No accent bar — unlike ScheduleTable's day
// header (where the bar's color distinguishes future vs. past), every row
// here is equally "affected," so a colored bar would carry no information,
// and the Collapse arrow already signals that the row expands.
const InstructorGroupHeader = ({
  instructorName,
  count,
}: {
  instructorName: string;
  count: number;
}) => (
  <Flex gap={8} align="center">
    <Typography.Title level={5} style={{ margin: 0, fontFamily: "var(--font-display)" }}>
      {instructorName}
    </Typography.Title>
    <Text style={{ color: colors.ink3, fontWeight: 500, fontSize: textPx(12.5) }}>
      · {count} {translate("class_settings.employee_management.reactivation_conflict.class_count")}
    </Text>
  </Flex>
);

// One instructor's day-grouped occurrences — the Collapse panel's body.
const InstructorGroupBody = ({ occurrences }: { occurrences: AffectedInstructorOccurrence[] }) => {
  const dayGroups = useMemo(() => groupByDate(occurrences), [occurrences]);
  return (
    <div>
      {dayGroups.map(([date, dayOccurrences]) => (
        <DayGroup key={date} date={date} occurrences={dayOccurrences} />
      ))}
    </div>
  );
};

const groupByInstructor = (
  affected: AffectedInstructorOccurrence[],
): [string, AffectedInstructorOccurrence[]][] => {
  const byInstructor = new Map<string, AffectedInstructorOccurrence[]>();
  affected.forEach((occurrence) => {
    byInstructor.set(occurrence.instructor_id, [
      ...(byInstructor.get(occurrence.instructor_id) ?? []),
      occurrence,
    ]);
  });
  return Array.from(byInstructor.entries()).sort(([, a], [, b]) =>
    a[0].instructor_name.localeCompare(b[0].instructor_name),
  );
};

const ReactivationWarningContent = ({
  preview,
}: {
  preview: EmployeeManagementReactivationPreview;
}) => {
  const instructorGroups = useMemo(() => groupByInstructor(preview.affected), [preview]);
  return (
    <div>
      <Text style={modalSubtitleStyle}>
        {translate("class_settings.employee_management.reactivation_conflict.description")}
      </Text>
      {/* All collapsed by default — every header still shows name + count,
          so the admin sees the full SCOPE (how many instructors, how many
          classes each) at a glance before drilling into any one of them,
          rather than one arbitrarily pre-opened section competing for
          attention with the summary. */}
      <Collapse
        ghost
        style={{ marginTop: 16 }}
        items={instructorGroups.map(([instructorId, occurrences]) => ({
          key: instructorId,
          label: (
            <InstructorGroupHeader
              instructorName={occurrences[0].instructor_name}
              count={occurrences.length}
            />
          ),
          children: <InstructorGroupBody occurrences={occurrences} />,
        }))}
      />
    </div>
  );
};

const hasConflicts = (preview: EmployeeManagementReactivationPreview) =>
  preview.affected.length > 0;

const EmployeeManagementReactivationWarningModal = ({
  open,
  preview,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  preview: EmployeeManagementReactivationPreview | null;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  // Same popup button-color system as LeaveConflictWarningModal: navy
  // proceed-anyway confirm, solid-ink "Batal" dismiss.
  <ConfigProvider theme={modalButtonsTheme}>
    <Modal
      open={open}
      title={
        <ModalHeader
          tone="amber"
          title={translate("class_settings.employee_management.reactivation_conflict.title")}
        />
      }
      okText={translate("class_settings.employee_management.reactivation_conflict.confirm_anyway")}
      cancelText={translate("button.cancel")}
      onOk={onConfirm}
      onCancel={onCancel}
      width={480}
      destroyOnClose
      // Collapsing all-but-the-first instructor keeps the INITIAL view short,
      // but an admin expanding several sections (or one instructor with many
      // days) can still grow past the viewport — cap the body in its own
      // scroll region rather than letting the whole page scroll under the
      // modal, keeping the title/footer always reachable.
      styles={{ body: { maxHeight: "60vh", overflowY: "auto" } }}
    >
      {preview && <ReactivationWarningContent preview={preview} />}
    </Modal>
  </ConfigProvider>
);

/**
 * Gates saving the Employee Management settings page behind a business-wide
 * scan, only when doing so would newly turn on working-hours and/or leave
 * enforcement. Mirrors useLeavePreview (LeaveConflictWarningModal.tsx)
 * exactly, one level of grouping deeper (instructor, then date):
 *  - checkAndConfirm(relevantReasons, onConfirm, onCancel?): runs the scan,
 *    then filters the response to the reasons actually newly relevant (a
 *    working-hours result is noise if only leave enforcement is newly
 *    turning on, and vice versa). Any filtered matches -> opens the warning
 *    modal, running onConfirm on confirm or onCancel on "Batal" (e.g. to
 *    revert the toggle(s) the admin just flipped back to their saved value,
 *    since nothing was actually saved); none -> runs onConfirm immediately.
 *    A failed check proceeds (same fail-open reasoning as useLeavePreview:
 *    informational only, never a hard block).
 *  - reactivationPreviewWarning: the modal element to render in the caller's
 *    JSX.
 *  - isChecking: true only while the scan itself is in flight (not while the
 *    resulting modal is open) — wire into a save button's own loading state
 *    so it can't be double-clicked while the check is pending, mirroring
 *    NewLeaveModal's isCheckingConflicts guard for the equivalent race on
 *    "Ajukan Cuti".
 */
export const useReactivationPreview = () => {
  const [pending, setPending] = useState<{
    preview: EmployeeManagementReactivationPreview;
    onConfirm: () => void;
    onCancel?: () => void;
  } | null>(null);
  // True only while the fetch itself is in flight — flips back to false the
  // instant it settles, whether that leads straight to onConfirm() or to
  // opening the warning modal. NOT tied to how long the admin spends looking
  // at that modal afterward (unlike `pending`, which stays set for that
  // whole time) — a caller wiring this into a save button's loading state
  // needs the button to stop spinning once the check resolves, not stay
  // stuck spinning for as long as the modal is open.
  const [isChecking, setIsChecking] = useState(false);

  const checkAndConfirm = useCallback(
    async (
      relevantReasons: Set<AffectedInstructorOccurrence["reasons"][number]>,
      onConfirm: () => void,
      onCancel?: () => void,
    ) => {
      setIsChecking(true);
      let preview: EmployeeManagementReactivationPreview;
      try {
        preview = await getEmployeeManagementReactivationPreview();
      } catch (error) {
        console.error(
          "Employee-management reactivation preview failed; proceeding without warning.",
          error,
        );
        setIsChecking(false);
        onConfirm();
        return;
      }
      setIsChecking(false);
      // An occurrence stays in if ANY of its reasons is newly relevant — it
      // can carry both, and one of them being the one that's newly turning
      // on is enough to warrant surfacing it (with all of its actual reasons
      // shown, not just the relevant one — see OccurrenceCard).
      const filtered: EmployeeManagementReactivationPreview = {
        affected: preview.affected.filter((occurrence) =>
          occurrence.reasons.some((reason) => relevantReasons.has(reason)),
        ),
      };
      if (!hasConflicts(filtered)) {
        onConfirm();
        return;
      }
      setPending({ preview: filtered, onConfirm, onCancel });
    },
    [],
  );

  const reactivationPreviewWarning = (
    <EmployeeManagementReactivationWarningModal
      open={!!pending}
      preview={pending?.preview ?? null}
      onConfirm={() => {
        pending?.onConfirm();
        setPending(null);
      }}
      onCancel={() => {
        pending?.onCancel?.();
        setPending(null);
      }}
    />
  );

  return { checkAndConfirm, reactivationPreviewWarning, isChecking };
};
